import { take } from 'rxjs';
import { Environment } from 'src/environments/environment';
import { Strings } from '../string/string';
import { LogLevel } from './logLevel';


enum Browser {
    Chrome = "Chrome",
    Firefox = "Firefox",
    Edge = "Edge",
    Opera = "Opera",
    Safari = "Safari"
}


export class Logger {

    private static loggingService: any

    public static set LoggingService(value: any) {
        Logger.loggingService = value
    }

    public static enabled(level: number = 0): boolean {
        return Environment.LogLevel <= level;
    }

    public static timeStamp(): string {
        return new Date().toLocaleTimeString('de-DE')
    }

    private static write(message: any, level: number = 0, ...params: any): void {

        const isObject = typeof message === 'object'
        let output = isObject ? '' : message

        if (Logger.enabled(level)) {
            const caller: string = Logger.getCallingFunction(['write', 'debug']) ?? "Unknown function"
            const logLevel: string = Strings.padAround(` ${LogLevel.getLevel(level)} `, 13, '▬');
            const time: string = `${Logger.timeStamp()}`
            const debugLine: string = `%c${time} %c[%c${logLevel}%c] [%c${caller}()%c] %c${output}`;
            const infoLine: string = `%c${time} %c[%c${logLevel}%c] %c- ${output}`;

            let line: string = infoLine

            const defaultFontWeight: string = "normal";
            const defaultColor: string = "black";
            const timeColor: string = "black";
            const defaultBackgroundColor: string = "inherit";

            let messageColor: string = "black";
            let modeBackgroundColor: string = defaultBackgroundColor
            let callerColor: string = "black";
            let fontWeight: string = "bold";
            let modeColor: string;

            switch (level) {
                case LogLevel.Trace:
                    modeColor = "hsla(289, 68%, 53%, 1)"
                    messageColor = "hsla(289, 33%, 53%, 1)"
                    line = debugLine
                    break;
                case LogLevel.Debug:
                    modeColor = "hsla(115, 100%, 37%, 1)"
                    messageColor = "hsla(115, 64%, 37%, 1)"
                    line = debugLine
                    break;
                case LogLevel.Info:
                    modeColor = "hsla(220, 69%, 50%, 1)"
                    messageColor = "hsla(220, 36%, 36%, 1)"
                    break;
                case LogLevel.Warning:
                    modeColor = "hsla(29, 81%, 53%, 1)"
                    messageColor = "hsla(29, 55%, 47%, 1)"
                    break;
                case LogLevel.Error:
                    modeColor = "hsla(0, 78%, 53%, 1)"
                    messageColor = "hsla(0, 55%, 53%, 1)"
                    break;
                case LogLevel.Critical:
                    modeColor = "white"
                    messageColor = "hsla(0, 55%, 53%, 1)"
                    modeBackgroundColor = "red"
                    break;
                default:
                    fontWeight = defaultFontWeight;
                    modeColor = messageColor
                    break;
            };

            const timeStyle: string = Logger.getStyle(timeColor, defaultFontWeight, defaultBackgroundColor)
            const bracketStyle = Logger.getStyle(defaultColor, fontWeight, modeBackgroundColor)
            const levelStyle: string = Logger.getStyle(modeColor, fontWeight, modeBackgroundColor)
            const messageStyle: string = Logger.getStyle(messageColor, defaultFontWeight, defaultBackgroundColor)
            const callerStyle: string = Logger.getStyle(callerColor, defaultFontWeight, defaultBackgroundColor)


            const parameters: Array<any> = [
                timeStyle,
                bracketStyle,
                levelStyle,
                bracketStyle
            ]

            if (level === LogLevel.Debug || level === LogLevel.Trace) {
                parameters.push(callerStyle, bracketStyle)
            }

            parameters.push(messageStyle)

            if (isObject) {
                parameters.push(message)
            }

            if (!!params?.length)
                parameters.push(...params)
            console.log(line, ...parameters)
        }
    }

    public static info(message: any, ...params: any): void {
        Logger.write(message, LogLevel.Info);
    }

    public static trace(message: any, ...params: any): void {
        Logger.write(message, LogLevel.Trace, ...params)
    }

    public static debug(message: any, ...params: any): void {
        Logger.write(message, LogLevel.Debug, ...params)
    }

    public static warning(message: any, ...params: any): void {
        Logger.write(message, LogLevel.Warning, ...params)
    }

    public static critical(message: any, ...params: any): void {
        Logger.write(message, LogLevel.Critical, ...params)
    }

    public static error(message: any, sendRemote: boolean = true, ...params: any): void {
        Logger.write(message, LogLevel.Error, ...params)
        if (sendRemote) {
            Logger.record(message, LogLevel.Error)
        }
    }

    // Send notifications of authentication failure attempts etc.
    public static record(message: string, logLevel: number = LogLevel.Error): void {
        Logger.debug('Remotely logging message', message)
        if (!Logger.loggingService) {
            Logger.debug('Logging Service Not Initialized')
        } else {
            Logger.loggingService.create(message, logLevel)
                .pipe(take(1))
                .subscribe((result: any) => {
                    Logger.debug(result)
                }, (error: any) => {
                    Logger.error('Error sending remote logs', false, error)
                })
        }
    }

    private static getStyle(textColor: string, fontWeight: string, backgroundColor: string) {
        return `color: ${textColor}; font-weight: ${fontWeight}; background-color: ${backgroundColor}`
    }

    public static getCallingFunction(exclude: Array<string> | undefined = undefined): string | undefined {
        const browser: Browser | undefined = Logger.getBrowser()
        const stack = new Error().stack?.split('\n')

        let returnValue: string | undefined
        switch (browser) {
            case Browser.Chrome:
                returnValue = Logger.getFunctionNameChrome(stack, exclude)
                break
            case Browser.Firefox:
                returnValue = Logger.getFunctionNameFirefox(stack, exclude)
                break

        }
        return returnValue
    }

    private static getFunctionNameChrome(stack: string[] | undefined, exclude: Array<string> | undefined = undefined): string | undefined {
        if (!stack) return Strings.Empty
        let row = stack.find((entry: string) => !entry.startsWith('Error') && !entry.includes('at Logger') && (!exclude || !exclude.some(value => entry.includes(value))))
        const prefix: string = row?.startsWith("    at new ") ? "    at new " : "    at ";
        row = row?.replace("Object.next", "Observable.subscribe")
        return row?.split(prefix)[1].split(' ')[0] ?? Strings.Empty
    }

    private static getFunctionNameFirefox(stack: string[] | undefined, exclude: Array<string> | undefined = undefined): string {
        const filter = exclude ?? []
        filter.push('getCallingFunction')
        let returnValue: string
        if (!stack) return Strings.Empty

        const lastDebugIndex = stack.findIndex(row => !filter.some(value => row.includes(value)))
        returnValue = lastDebugIndex < 1 ? Strings.Empty : stack[lastDebugIndex]
        return returnValue?.split('@')[0].split('/')[0] ?? Strings.Empty
    }


    private static getBrowser(): Browser | undefined {
        let returnValue: Browser | undefined
        const userAgent = navigator.userAgent

        switch (true) {
            case userAgent.includes(Browser.Chrome):
                returnValue = Browser.Chrome
                break
            case userAgent.includes(Browser.Edge):
                returnValue = Browser.Edge
                break
            case userAgent.includes(Browser.Firefox):
                returnValue = Browser.Firefox
                break
            case userAgent.includes(Browser.Opera):
                returnValue = Browser.Opera
                break
            case userAgent.includes(Browser.Safari):
                returnValue = Browser.Safari
                break
        }
        return returnValue
    }
};
